"use strict";
Object.defineProperty(exports, Symbol.toStringTag, { value: "Module" });
const gitReleaseTag$l = "1.0.0";
const AlertDocumentation = {
  name: "Alert",
  description: "Alerts are used to display important messages or notifications to users within the website, without interrupting. It includes features a title, a short message and an icon to indicate the message type (like warning or success).",
  status: "stable",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$l}`,
  gitReleaseTag: gitReleaseTag$l,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/feedback/Alert",
  safariLink: "https://confluence.grandvision.global/display/GV/Alert",
  storybookLink: "?path=/docs/components-feedback-alert--docs",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?m=auto&node-id=24244%3A23301",
  accessibility: `If the alert requires immediate user attention, it should include <code>role="alert"</code> to inform assistive technologies. This role is used by default on the error variant of the alert and cannot be changed. For other variants, if you need to notify screen reader users, set the notifyScreenReader property to <code>true</code>. <br/><br/>
  <a href="https://w3c.github.io/aria/#alert">More information about the alert role</a><br/><br/>
  If the content of the note is advisory information not important enough to justify an alert, <code>role="status"</code> should be used (standard for all variants except error). When added to an element, the browser will send out an accessible status event to assistive technologies, which can then notify the user about it. <br/><br/>
  By default, icons indicating the type of alert should not be read by screen readers, as they are purely informative. These icons should have <code>aria-hidden="true"</code> applied to them. <br/><br/>
  <a href="https://w3c.github.io/aria/#status">More information about the status role</a><br/><br/>
  <strong>Keyboard interaction</strong><br/><br/>
  If the alert contains interactive elements, they must be accessible via the keyboard. If it only contains text, it should not have any keyboard interactions.`
};
const gitReleaseTag$k = "1.10.0";
const badgeData = {
  status: "stable",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=22800-31554",
  gitReleaseTag: gitReleaseTag$k,
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$k}`,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/action/Badge/",
  safariLink: "https://confluence.grandvision.global/display/GV/Badge",
  accessibility: `
  <p>
    Badges are used to activate and deactivate filter options.
  </p>

  <h3>Accessibility</h3>
  <p> Ensure badges are clearly identified for screen readers. You can use one of three badge variants: BadgeCheckbox, BadgeRadio, or BadgeButton. When using BadgeRadio or BadgeCheckbox, place them inside a RadioGroup or CheckboxGroup with a required group label. If you opt for BadgeButton, make sure it has an accessible name. By default, this name comes from the button's text content, but you can also specify it using <code>aria-labelledby</code> or <code>aria-label</code>. 
  </p>

  <h3>Keyboard Interaction</h3>
  <ul>
    <li>
      <strong>BadgeCheckbox:</strong> 
      <ul>
        <li>BadgeCheckboxes can be focused using the <code>Tab</code> key.</li>
        <li>Actions are triggered by pressing <code>Space</code> or <code>Enter</code>.</li>
      </ul>
    </li>
    <li>
      <strong>BadgeRadio:</strong> 
      <ul>
        <li>BadgeRadios should be placed in a RadioGroup which follows <a href="https://www.w3.org/WAI/ARIA/apg/patterns/radio/" target="_blank">this pattern</a></li>
      </ul>
    </li>
        <li>
      <strong>BadgeButton:</strong> 
      <ul>
        <li>Buttons can be focused using the <code>Tab</code> key.</li>
        <li>Actions are triggered by pressing <code>Space</code> or <code>Enter</code>. Make sure you use the <code>onPress</code> prop</li>
      </ul>
    </li>
  `
};
const Global$4 = {
  ...badgeData,
  name: "Badge",
  description: "Badges are used to activate and deactivate filter options.",
  storybookLink: "?path=/docs/components-action-badge-badgebutton--docs"
};
const BadgeButton = {
  ...badgeData,
  name: "BadgeButton",
  description: "A BadgeButton is a button styled like a badge but functions as a standard button. It is not toggleable and is typically used for actions like applying filters, triggering events, or navigating, while maintaining the compact and pill-shaped appearance of a badge.",
  storybookLink: "?path=/docs/components-action-badge-badge-button--docs"
};
const BadgeRadio = {
  ...badgeData,
  name: "BadgeRadio",
  compoundComponents: "RadioGroup",
  description: "A badge component is used for filtering that allows users to activate or deactivate filters. A BadgeCheckbox is used for selecting a single option. It visually indicates selected and unselected states.",
  storybookLink: "?path=/docs/components-action-badge-badge-radio--docs",
  usage: "Designed to be used within a <code>RadioGroup</code>. The <code>defaultValue</code> prop in <code>RadioGroup</code> sets the initial state (uncontrolled), while the <code>onChange</code> prop fires when a user selects or deselects an option."
};
const BadgeCheckbox = {
  ...badgeData,
  name: "BadgeCheckbox",
  compoundComponents: "CheckboxGroup",
  description: "A badge component is used for filtering that allows users to activate or deactivate filters. A BadgeCheckbox is used for selecting a single option. It visually indicates selected and unselected states.",
  storybookLink: "?path=/docs/components-action-badge-badge-checkbox--docs",
  usage: "A checkbox styled as a badge for multi-selection, designed to be used within a <code>CheckboxGroup</code> for accessibility. The <code>defaultValue</code> prop in <code>CheckboxGroup</code> sets the initial state (uncontrolled), while the <code>onChange</code> prop fires when a user selects or deselects an option."
};
const BadgeDocumentation = {
  Global: Global$4,
  BadgeButton,
  BadgeRadio,
  BadgeCheckbox
};
const gitReleaseTag$j = "1.9.0";
const buttonData = {
  status: "beta",
  gitReleaseTag: gitReleaseTag$j,
  description: "Buttons allow users to take actions or navigate to different pages. They can also be rendered as anchors within a button element.",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$j}`,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/action/Button/Button.tsx",
  safariLink: "https://confluence.grandvision.global/display/GV/Button",
  accessibility: `
  <p>
    Buttons are interactive elements used to trigger actions or events, such as submitting a form, opening a dialog, canceling an operation, or deleting an item. 
    <strong>Buttons are not intended for navigation to web pages</strong>—use a link or a button styled as an anchor (<code>&lt;a&gt;</code>) instead.
  </p>

  <h3>Accessibility</h3>
  <ul>
    <li>
      <strong>Accessible Label:</strong> 
      Buttons must have an accessible name. By default, this name is will come from the text content inside the button element. Or you can specify the name using 
      <code>aria-labelledby</code> or <code>aria-label</code>.
    </li>
  </ul>

  <h3>Loading State</h3>
  <p>
    If a button has a loading state:
  </p>
  <ul>
    <li>The <code>aria-busy="true"</code> attribute will be automatically applied to indicate it is busy.</li>
    <li>The button will get an <code>aria-disabled="true"</code> added to it
    <li>When the button is set to <code>isLoading="true"</code>, you can fill in the <code>isLoadingDescription</code> prop to provide a textual description that will be announced by screen readers</li>
  </ul>

  <h3>Keyboard Interaction</h3>
  <ul>
    <li>
      <strong>Standard Buttons:</strong> 
      <ul>
        <li>Buttons can be focused using the <code>Tab</code> key.</li>
        <li>Actions are triggered by pressing <code>Space</code> or <code>Enter</code>. Make sure you use the <code>onPress</code> prop</li>
      </ul>
    </li>
    <li>
      <strong>Links Styled as Buttons:</strong> 
      <ul>
        <li>Actions are triggered by pressing <code>Space</code> since it doesn't have the role="button"</code>.
      </ul>
    </li>
  </ul>`,
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?m=auto&node-id=16963-11775"
};
const Global$3 = {
  ...buttonData,
  name: "Button",
  storybookLink: "?path=/docs/components-action-button-button-primary--docs"
};
const ButtonPrimary = {
  ...buttonData,
  name: "ButtonPrimary",
  storybookLink: "?path=/docs/components-action-button-button-primary--docs"
};
const ButtonSecondary = {
  ...buttonData,
  name: "ButtonSecondary",
  storybookLink: "?path=/docs/components-action-button-button-secondary--docs"
};
const ButtonOutline = {
  ...buttonData,
  name: "ButtonOutline",
  storybookLink: "?path=/docs/components-action-button-button-outline--docs"
};
const ButtonGhost = {
  ...buttonData,
  name: "ButtonGhost",
  storybookLink: "?path=/docs/components-action-button-button-ghost--docs"
};
const ButtonDocumentation = {
  Global: Global$3,
  ButtonPrimary,
  ButtonSecondary,
  ButtonOutline,
  ButtonGhost
};
const gitReleaseTag$i = "3.0.0";
const CheckboxDocumentation = {
  accessibility: `The checkbox supports full keyboard navigation, allowing selection with Enter or Space. If no label is provided, <code>aria-label</code> or <code>aria-labelledby</code> must be set, otherwise a console error is shown.<br/><br/>
    If you need a user to select multiple items from a list of options, consider using a <code>CheckboxGroup</code> component around the Checkboxes`,
  description: "A checkbox lets users toggle one or more options on or off.",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=39419-15174&m=dev",
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/blob/main/iris-ui/src/components/input/Checkbox/Checkbox.tsx",
  gitReleaseTag: gitReleaseTag$i,
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$i}`,
  name: "Checkbox",
  safariLink: "https://confluence.grandvision.global/x/9KZpDQ",
  status: "beta",
  storybookLink: "https://iris-ui.grandvision.io/storybook/?path=/docs/components-checkbox--docs",
  usage: "The Checkbox can be used as a standalone checkbox or within a CheckboxGroup for multiple selections. It supports both controlled and uncontrolled states"
};
const gitReleaseTag$h = "3.0.0";
const CheckboxGroupDocumentation = {
  accessibility: "Each checkbox within the group requires a visible text label for clarity. The checkbox group itself must be identified to assistive technology through a label. In case you don`t want to show the label, you can use the `showLabel` prop to hide it visually.",
  description: "A checkbox group allows a user to select multiple items from a list of options.",
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/blob/main/iris-ui/src/components/input/Checkbox/CheckboxGroup.tsx",
  gitReleaseTag: gitReleaseTag$h,
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$h}`,
  name: "CheckboxGroup",
  safariLink: "https://confluence.grandvision.global/x/9KZpDQ",
  status: "beta",
  storybookLink: "https://iris-ui.grandvision.io/storybook/?path=/docs/components-checkbox--docs",
  usage: `Use <code>CheckboxGroup</code> to group multiple checkboxes together.<br/><br/>
  `
};
const gitReleaseTag$g = "1.10.0";
const DialogDocumentation = {
  name: "Dialog",
  description: 'A dialog is a "conversation" between the system and the user. It is prompted when the system needs input from the user or to give the user urgent information concerning their current workflow.',
  status: "beta",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$g}`,
  gitReleaseTag: gitReleaseTag$g,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/overlay/Dialog",
  safariLink: "https://confluence.grandvision.global/display/GV/Dialog",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=25255-6031",
  accessibility: `The Dialog component handles focus management and keyboard interactions automatically. When opened, the content behind the Dialog is hidden from assistive technologies and it's not possible to scroll the content. Focus is moved to the first focusable element inside the Dialog. When closed, the focus is restored to the element that triggered the Dialog. 
  If this behavior is not needed, you can disable it by setting the restoreFocus prop to false and manage focus manually in the onClose function.<br/>
  <br/>
  The focus is trapped within the Dialog to prevent users from tabbing outside of it.`,
  storybookLink: "?path=/docs/components-overlay-Dialog--docs",
  usage: `The Dialog component is used to confirm an action that the user is about to take. It is typically used when the user is about to delete something or perform an action that cannot be undone. The Dialog component renders a dialog with a title, a message, and two buttons: one for confirming the action and one for canceling it. The onConfirm function is called when the user clicks the confirm button, and the onClose function is called when the user clicks the cancel button or closes the dialog.`
};
const gitReleaseTag$f = "1.14.0";
const DrawerDocumentation = {
  name: "Drawer",
  description: "The Drawer component can be used as a hidden off-canvas sidebar used to display additional content without navigating away from the current view. It can be used to display additional information, navigation links, or any other content that is not essential to the main view.",
  status: "beta",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$f}`,
  gitReleaseTag: gitReleaseTag$f,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/overlay/Drawer",
  safariLink: "https://confluence.grandvision.global/spaces/GV/pages/187663358/Drawer",
  storybookLink: "?path=/docs/components-overlay-drawer--docs",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=32092-910&m=dev",
  accessibility: `The Drawer component handles focus management and keyboard interactions automatically. 
When opening the Drawer with a keyboard, focus will automatically move to the next focusable element inside the dialog. 
The <code>Escape</code> key can be used to close the Drawer, and focus will be restored to the element that triggered the Drawer (which is set to true by default).
<br/><br/>
By default, the Drawer is configured to act as an additional content area and uses a semantic <code>aside</code> HTML element. 
In this case, when the Drawer is open and a user tabs outside of it, the Drawer will close and focus will move to the next focusable element outside the Drawer.
<br/><br/>
If you want to make it a dialog, you can set the <code>isDialog</code> prop to true. This will add <code>role="dialog"</code> and a focus trap to the Drawer. This should be used only if the Drawer requires immediate user attention.
<br/><br/>
If the content needs to be available in the DOM for SEO purposes, you can set the <code>shouldMount</code> prop to true. This will render the Drawer content on load, but it will not be visible to screen readers until the Drawer is opened.
  `,
  usage: `Use the drawer component to display additional content without navigating away from the current view. If the Drawer requires user attention, set the <code>isDialog</code> prop to true. 
  For example to display a checkout summary, where the user cannot interact with the rest of the site until they close it.<br/><br/>
  Do you want to offer a choice? Where the user can either close or confirm the action? Use the <code>Dialog</code> component instead.`
};
const gitReleaseTag$e = "1.4.0";
const FontLoaderDocumentation = {
  name: "FontLoader",
  description: "The FontLoader component is used to serve & maintain the fonts within one location. It serves the fonts as a link and style element",
  status: "stable",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$e}`,
  gitReleaseTag: gitReleaseTag$e,
  storybookLink: "?path=/docs/components-utility-fontloader--docs",
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/utility/FontLoader/FontLoader.tsx"
};
const gitReleaseTag$d = "1.7.0";
const IconButtonDocumentation = {
  name: "IconButton",
  description: "The IconButton is a button that displays only an icon, with style options including primary, outline, ghost and elevated, making it versatile for various UI needs.",
  status: "stable",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$d}`,
  gitReleaseTag: gitReleaseTag$d,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/action/IconButton",
  safariLink: "https://confluence.grandvision.global/display/GV/Icon+Button",
  storybookLink: "?path=/docs/components-action-iconbutton--docs",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?m=auto&node-id=26304-12739",
  accessibility: `
  The IconButton lack visible text, which makes them challenging for users who rely on assistive technologies. Use the <code>aria-label</code> attribute to describe the button’s action. For example, if the button closes a modal, set <code>aria-label="Close modal"</code>.<br/>
  <br/>
  If an IconButton toggles the visibility of content (e.g., a dropdown, accordion, or modal), use the <code>aria-expanded</code> attribute to indicate the current state of the content. This helps users who rely on screen readers understand whether the associated content is currently shown or hidden.
  <br/>
  <br/>When interacting with a button using the keyboard, the button is triggered using the space or enter key when the button is focused. You should always use the <code>onPress</code> attribute to pass in any function you want to execute.`
};
const gitReleaseTag$c = "1.0.0";
const IconDocumentation = {
  name: "Icon",
  description: "Icons are symbols that represent a concept, word or instruction. We use them to clarify a sentence or word in the website",
  status: "stable",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$c}`,
  gitReleaseTag: gitReleaseTag$c,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/foundations/icons",
  safariLink: "https://confluence.grandvision.global/display/GV/Icon",
  storybookLink: "?path=/docs/foundations-icons--docs",
  figmaUrl: "https://www.figma.com/design/pz8hGNE4BNs8FHIu69c2yz/00-Foundations?node-id=21201-14103"
};
const gitReleaseTag$b = "1.0.0";
const MessageDocumentation = {
  name: "Message",
  description: "Messages are used to show information to users within the website without any interruption. Messages are less intrusive than alerts. It can include a title, a short message and a custom icon.",
  status: "stable",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=26257-8173",
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/feedback/Message",
  safariLink: "https://confluence.grandvision.global/display/GV/Message",
  storybookLink: "?path=/docs/components-feedback-message--docs",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$b}`,
  gitReleaseTag: gitReleaseTag$b,
  accessibility: `The WAI-ARIA role is not set by default on the message. For messages that need immediate user attention, it is advised to use the Alert component instead. The icon on the message is for decorative purposes only and is therefore hidden from screen readers. If the content of the note is advisory information not important enough to justify an alert, use <code>role="status"</code>. When added to an element, the browser will send out an accessible status event to assistive technologies, which can then notify the user about it. <br/><br/>
  <strong>Keyboard interaction</strong><br/><br/>
  If the alert contains interactive elements, they must be accessible via the keyboard. If it only contains text, it should not have any keyboard interactions.`
};
const gitReleaseTag$a = "1.5.0";
const Global$2 = {
  name: "Modal",
  status: "stable",
  description: "This component serves as the foundation for other overlay components like Drawer and Dialog. It is not meant to be used directly, but as a base for other overlay components. A modal is made up of two key elements: a backdrop and a modal container. The backdrop displays as a semi-transparent layer that covers the screen, blocking interaction with the content behind it. The overlay container can contain components such as dialogs, drawers, or other custom elements. Check first if you can use the <strong>Dialog</strong> or <strong>Drawer</strong> components before using the Modal component.",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$a}`,
  gitReleaseTag: gitReleaseTag$a,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/overlay/Modal",
  safariLink: "https://confluence.grandvision.global/display/GV/Modal"
};
const ModalDocumentation = {
  ...Global$2,
  name: "Modal",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=25255-5947&node-type=frame&t=pkeYW8bJECEhxtuj-0",
  accessibility: `The modal component handles focus management and keyboard interactions automatically. When opened, the content behind the modal is hidden from assistive technologies and it's not possible to scroll the content. Focus is moved to the first focusable element inside the modal. When closed, the focus is restored to the element that triggered the modal. 
  If this behavior is not needed, you can disable it by setting the restoreFocus prop to false and manage focus manually in the onClose function.<br/>
  <br/>
  By default, the focus is trapped within the modal to prevent users from tabbing outside of it. This behavior can be disabled by setting the focusTrap prop to false. Additionally, the modal can be closed by pressing the Escape key. Also if this is not desired, this can be disabled by setting the isKeyboardDismissable prop to false.`,
  usage: `Only use the Modal component if you are not able to use the <strong>Dialog</strong> or <strong>Drawer</strong> components.<br/>
  <br/>

  Use the <strong>ModalContainer</strong> component as a child of the Modal component to display the content. This ensures the modal has the correct behavior, such as trapping focus and handling dismissals. By default, the Modal and ModalContainer don't have any styles applied to them, which makes them suitable for any custom component that needs a backdrop and is dismissible.<br/><br/>
    Besides the ModalContainer we also have a standardized <strong>ModalHeader</strong> and <strong>ModalFooter</strong> components that can be used to add a header and footer to the modal. These components are optional. The ModalHeader has a close button by default. When setting the isDismissible prop to false on the Modal, this button will be hidden<br/><br/>
    `,
  compoundComponents: "ModalContainer, ModalHeader, ModalFooter",
  storybookLink: "?path=/docs/components-overlay-modal--docs"
};
const gitReleaseTag$9 = "1.2.0";
const Global$1 = {
  name: "Price",
  description: "Use the price component to display price information with consistent styling. The Foundation component is the base for the Price component and is not being exposed by itself. Use the PriceFull, PriceNew, and PriceOld components instead.",
  status: "stable",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$9}`,
  gitReleaseTag: gitReleaseTag$9,
  storybookLink: "?path=/docs/components-typography-price-foundation--docs",
  accessibility: `When using the Price component, ensure that the price is always announced to screen readers. This can be done by adding an aria-label attribute to the component. The aria-label should describe the price, for example, "The discounted price is" or "The original price was".`,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/typography/Price/Price.tsx"
};
const Price = {
  ...Global$1,
  imports: "",
  showArgsTable: false,
  figmaUrl: "https://www.figma.com/design/pz8hGNE4BNs8FHIu69c2yz/00-Foundations?node-id=4100-1919&m=dev",
  safariLink: "https://confluence.grandvision.global/display/GV/Price"
};
const PriceNew = {
  ...Global$1,
  name: "PriceNew",
  description: "The PriceNew component shows the current or reduced price, styled in a way to emphasize savings."
};
const PriceOld = {
  ...Global$1,
  name: "PriceOld",
  description: "The PriceOld component represents the previous price before a discount, displayed with a strikethrough to indicate it is no longer the current price."
};
const PriceFull = {
  ...Global$1,
  name: "PriceFull",
  description: "The PriceFull component displays the full, standard price of a product or service."
};
const PriceDocumentation = {
  Global: Global$1,
  Price,
  PriceNew,
  PriceOld,
  PriceFull
};
const gitReleaseTag$8 = "1.7.0";
const ProgressDocumentation = {
  name: "Progress",
  description: "The progress HTML element displays an indicator showing the completion progress of a task, typically displayed as a progress bar",
  status: "stable",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=26257-8173",
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/feedback/Progress",
  safariLink: "https://confluence.grandvision.global/display/GV/Progress",
  storybookLink: "?path=/docs/components-feedback-Progress--docs",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$8}`,
  gitReleaseTag: gitReleaseTag$8,
  accessibility: 'The <code>progress</code> element represents the completion of a task and is vital for accessibility. To enhance its usability, include the value and max attributes, which specify the current and maximum progress, respectively. Adding text content, like “75%”, allows screen readers to convey progress effectively.<br/><br/>For greater accessibility, use ARIA attributes: aria-valuenow (value) to indicate the current progress, aria-valuemax (max) to define the maximum value, and aria-labelledby (label) to associate a label with the progress element. An ID must be provided, to ensure the element is still understandable through descriptive text, in our case the <code>span</code>.<br/> While customizing the visual style of <code>progress</code>, maintain clarity for all users, including those who rely on visual cues.<br/><br/>You can read the docs <a href="https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/progressbar_role">here</a>'
};
const gitReleaseTag$7 = "1.3.0";
const SkeletonDocumentation = {
  name: "Skeleton",
  description: `A skeleton loader serves as a placeholder while content is being loaded. It provides a visual cue to users that data is being fetched and the content they expect will appear soon. This technique helps in managing user expectations and improving the perceived performance of an application or website.`,
  status: "stable",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$7}`,
  gitReleaseTag: gitReleaseTag$7,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/feedback/Skeleton",
  safariLink: "https://confluence.grandvision.global/display/GV/Skeleton",
  storybookLink: "?path=/docs/components-feedback-skeleton--docs",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=24244-23135",
  usage: ` The skeleton takes SVG elements as children to create the placeholder. It currently accepts <code>rect</code>, <code>circle</code>, <code>path</code>, <code>ellipse</code>, and <code>line</code> elements. The height of the skeleton is set by the <code>height</code> prop.<br/><br/>
  To use it, ask a designer to export the SVG from Figma. Once exported, simply set the desired height and add the SVG elements inside the Skeleton component.`,
  accessibility: `The skeleton acts as a temporary placeholder while content is loading, which should be clear to screen readers. The SVG has an <code>aria-busy="true"</code> attribute applied by default, signaling that the content is still loading and that the user should wait. When only one Skeleton Loader is visible, it’s important to add an <code>aria-label</code> to inform screen reader users that content is being loaded. If there are multiple loading indicators, be mindful of how many announcements are made to screen readers. The best practice in that scenario is to add <code>aria-hidden="true"</code> to the repeating skeletons and include a visually hidden <code>span</code> that announces the content is loading, <a href="/?path=/story/components-feedback-skeleton--accessibility">ensuring a single announcement</a> for all skeletons.<br/><br/>
  When loading is complete, the skeleton should be replaced by the actual content, and a message should be read out to screen reader users that the content has been loaded. For users sensitive to motion, the animation has been disabled if they have enabled the "reduce motion" setting in their operating system.<br/><br/>
  <strong>Keyboard interaction</strong><br/>
  The skeleton does not have any keyboard interactions, as it is a visual element only.`
};
const gitReleaseTag$6 = "1.11.0";
const SpacingDocumentation = {
  name: "spacingHelper",
  status: "stable",
  showArgsTable: false,
  description: `The spacingHelper is a function that can be used for setting margin to components in iris-ui, but it can also be used on the implementation side. 
It only allows a strict set of steps from 0 to 11, this way the spacing is aligned with Figma.`,
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$6}`,
  gitReleaseTag: gitReleaseTag$6,
  storybookLink: "?path=/docs/components-utility-spacing--docs",
  imports: `import { spacingHelper } from '@grandvisionhq/iris-ui'`,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/utility/spacing",
  safariLink: "https://confluence.grandvision.global/display/GV/Spacing"
};
const gitReleaseTag$5 = "1.1.0";
const SpinnerDocumentation = {
  name: "Spinner",
  description: "A visual element used to indicate that a process or task is currently in progress and that the user should wait until it is completed. It is typically used during asynchronous operations, such as data fetching, file uploads, or other background tasks, to provide feedback to the user that the application is working on their request.",
  status: "stable",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$5}`,
  gitReleaseTag: gitReleaseTag$5,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/feedback/Spinner",
  safariLink: "https://confluence.grandvision.global/display/GV/Spinner",
  storybookLink: "?path=/docs/components-feedback-spinner--docs",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=24054-23554",
  accessibility: `
    A loader indicates a status, so it has the <code>role="status"</code> applied to it by default.
    However, there are instances, such as when multiple loaders are displayed on a page, where this role should be disabled. 
    To do this, set the <code>status</code> property to <code>false</code>. 
    When disabling the status role, ensure that an <code>aria-label</code> is used on the spinner component to inform users that something is currently loading on the page.
    <br/><br/>
    <a href="https://w3c.github.io/aria/#status">More information about the status role</a><br/><br/>
    It's important to notify users when loading is complete, particularly if the loading takes more than a few seconds. 
    Users with screen readers may not notice the disappearance of the loading indicator. 
    This can be addressed by shifting focus to a relevant element once loading is finished. 
    If changing focus is not possible, a non-displayed message can be used to communicate the completion of loading to assistive technology users.
    `
};
const gitReleaseTag$4 = "1.11.0";
const ToastDocumentation = {
  name: "Toast",
  description: "a toast is a small, non-intrusive, temporary notification that provides feedback or updates to the user. It is displayed for a short period of time and then disappears. Toasts are designed to be seen without needing immediate action, improving user experience by showing important information briefly. The toast is never used as a final state, the Alert is.",
  status: "beta",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$4}`,
  gitReleaseTag: gitReleaseTag$4,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/src/components/overlay/Toast/",
  safariLink: "https://confluence.grandvision.global/display/GV/Toast",
  storybookLink: "?path=/docs/components-overlay-toast--docs",
  usage: `To use the Toast component, you need to wrap your application in the <code>ToastProvider</code> component. This will provide the context for the Toast component to work. The <code>ToastProvider</code> handles the state of the toasts and the methods to show them.<br/>
    <br/>
    After the <code>ToastProvider</code> is set up, you can use the <code>useToast</code> hook. 
    The <code>useToast</code> hook provides a set of functions to display different types of toast notifications. It can be used to show success, error, warning, and info toasts.
    <ul>
      <li><code>toast.success('it was succesfull', 3000)</code></li>
      <li><code>toast.error('there was an error')</code></li>
      <li><code>toast.warning('something was wrong')</code></li>
      <li><code>toast.info('just to inform you')</code></li>
    </ul>
    The <code>message</code> argument is the text that will be displayed in the toast, <code>timeout</code> is the time in milliseconds that the toast will be displayed. If no timeout is provided, the default timeout of 5000 will be used.<br/>
    <br/>
   The Toast itself has a <code>closeButtonDescription</code> prop that is used to provide an aria-label for the close button. This can't be filled by using the toast methods, it needs to be passed in as a prop via the <code>ToastProvider</code>.`,
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=33589-6239",
  imports: `import { useToast } from '@grandvisionhq/iris-ui' <br/><br/> const { toast } = useToast()`,
  accessibility: `Toasts can be easy to miss since they disappear automatically. Because of this, their content should always be non-essential for completing a task.
  If users need more time to read a toast, the timeout duration can be extended, or they can hover over it with their mouse to pause the timer. Auto-dismissed toasts also include a visual timer, indicating how much time remains before they disappear.`
};
const gitReleaseTag$3 = "1.2.0";
const ToggletipDocumentation = {
  name: "Toggletip",
  description: "Toggletips are non-modal dialogs that float around a trigger. They are meant as a brief, informative message that appears when a user interacts with an element. This is useful when only 1 or 2 sentences additional copy is required for explanation",
  status: "stable",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$3}`,
  gitReleaseTag: gitReleaseTag$3,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/overlay/Toggletip",
  safariLink: "https://confluence.grandvision.global/display/GV/Toggletip",
  storybookLink: "?path=/docs/components-toggletip--docs",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?m=auto&node-id=21932-46434",
  accessibility: `
    The accessibility guidelines for the toggletip are as follows:
    <ul>
      <li>The icon button should have an appropriate aria-label (e.g., “show information”) to ensure accessibility.</li>
      <li>The SVG within the button should have aria-hidden="true" to prevent it from being read by screen readers.</li>
      <li>The button should reference the popover element using aria-haspopup, indicating the availability and type of interactive popup element </li>
      <li>When the popover is visible, the button should have aria-expanded="true", and when the popover is hidden, it should have aria-expanded="false".</li>
      <li>When you pass the showLabel property it removes the required aria-label and passes it as a p element in the explainer.</li>
    </ul>
    Currently we only support text in the Toggletip so links are not supported.<br/><br/>
    <strong>Keyboard interaction</strong><br/><br/>
    The icon button should be focusable and operable using the keyboard.
    <ol>
      <li>Pressing Enter or Spacebar should open the popover. </li>
      <li>Pressing Escape should close it.</li>
      <li>If the popover is open, pressing Tab should close the tab.</li>
      <li>The content inside the popover should be accessible to screen readers and include a visible focus state (focus ring) when focused.</li>
    </ol>`
};
const TypographyStatus = "stable";
const gitReleaseTag$2 = "1.0.0";
const Global = {
  name: "Typography",
  description: "Typography is used as a foundation to display text",
  status: TypographyStatus,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/foundations/typography/Text",
  gitReleaseTag: gitReleaseTag$2,
  storybookLink: "?path=/docs/foundations-typography-text--docs"
};
const Text = {
  name: "Text",
  description: `The text component is a generic component that can be used to render text in any of the allowed HTML elements (component types), with predefined typography styles. 
    It doesn't come with other styling than font-family, font-weight, font-sizes and line-height.<br/><br/>
    We export some helper components that are used to render text in specific HTML elements, with default typography styling applied to it:<br/>
    <a href="/?path=/docs/foundations-typography-h1--docs"><code>H1</code></a>, 
    <a href="/?path=/docs/foundations-typography-h2--docs"><code>H2</code></a>, 
    <a href="/?path=/docs/foundations-typography-h3--docs"><code>H3</code></a>, 
    <a href="/?path=/docs/foundations-typography-h4--docs"><code>H4</code></a>, 
    <a href="/?path=/docs/foundations-typography-h5--docs"><code>H5</code></a>, 
    <a href="/?path=/docs/foundations-typography-h6--docs"><code>H6</code></a>, 
    <a href="/?path=/docs/foundations-typography-p--docs"><code>P</code></a>, 
    <a href="/?path=/docs/foundations-typography-Small--docs"><code>Small</code></a>, 
    <a href="/?path=/docs/foundations-typography-Strong--docs"><code>Strong</code></a><br/><br/>
    It is possible to overwrite the default styling by passing the <code>variant</code> prop to the <code>Text</code> component.<br/>`,
  imports: "import { Text } from '@grandvisionhq/iris-ui'",
  safariLink: "https://confluence.grandvision.global/display/GV/Typography",
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/foundations/typography/Text",
  figmaUrl: "https://www.figma.com/design/pz8hGNE4BNs8FHIu69c2yz/00-Foundations?node-id=4100-1919&t=ak8Pz3d8k79z9hjU-0",
  status: TypographyStatus
};
const H1 = {
  name: "H1",
  description: "This is a helper component that renders text in an <code>h1</code> tag with predefined typography styles. You can overwrite the default styling by passing the <code>variant</code> prop to the <code>H1</code> component. It is advised to only use the heading variants for H1-H6 tags.",
  status: TypographyStatus
};
const H2 = {
  name: "H2",
  description: "This is a helper component that renders text in an <code>h2</code> tag with predefined typography styles. You can overwrite the default styling by passing the <code>variant</code> prop to the <code>H2</code> component. It is advised to only use the heading variants for H1-H6 tags.",
  status: TypographyStatus
};
const H3 = {
  name: "H3",
  description: "This is a helper component that renders text in an <code>h3</code> tag with predefined typography styles. You can overwrite the default styling by passing the <code>variant</code> prop to the <code>H3</code> component. It is advised to only use the heading variants for H1-H6 tags.",
  status: TypographyStatus
};
const H4 = {
  name: "H4",
  description: "This is a helper component that renders text in an <code>h4</code> tag with predefined typography styles. You can overwrite the default styling by passing the <code>variant</code> prop to the <code>H4</code> component. It is advised to only use the heading variants for H1-H6 tags.",
  status: TypographyStatus
};
const H5 = {
  name: "H5",
  description: "This is a helper component that renders text in an <code>h5</code> tag with predefined typography styles. You can overwrite the default styling by passing the <code>variant</code> prop to the <code>H5</code> component. It is advised to only use the heading variants for H1-H6 tags.",
  status: TypographyStatus
};
const H6 = {
  name: "H6",
  description: "This is a helper component that renders text in an <code>h6</code> tag with predefined typography styles. You can overwrite the default styling by passing the <code>variant</code> prop to the <code>H6</code> component. It is advised to only use the heading variants for H1-H6 tags.",
  status: TypographyStatus
};
const P = {
  name: "P",
  description: "This is a helper component that renders text in an <code>p</code> tag with predefined typography styles. You can overwrite the default styling by passing the <code>variant</code> prop to the <code>P</code> component. It is advised to only use the text-body or price variants.",
  status: TypographyStatus
};
const Small = {
  name: "Small",
  description: "This is a helper component that renders text in an <code>small</code> tag with predefined typography styles. You can overwrite the default styling by passing the <code>variant</code> prop to the <code>Small</code> component. It is advised to only use the text-caption or price-small variants. Use the Small component only for inline text inside a paragraph, to make sure the right line-height is applied. If you need it as a standalone component, use the <code>P</code> or the <code>Figcaption</code> component instead.",
  status: TypographyStatus
};
const Strong = {
  name: "Strong",
  description: "This is a helper component that renders text in an <code>strong</code> tag with predefined typography styles. You can overwrite the default styling by passing the <code>variant</code> prop to the <code>Strong</code> component. It is advised to only use the text or price variants. It is advised to only use the text-caption or price-small variants. Use the Strong component only for inline text inside a paragraph, to make sure the right line-height is applied. If you need it as a standalone component, use the <code>P</code> instead.",
  status: TypographyStatus
};
const Figcaption = {
  name: "Figcaption",
  description: "This is a helper component that renders text in an <code>figcaption</code> tag with predefined typography styles. You can overwrite the default styling by passing the <code>variant</code> prop to the <code>Strong</code> component. It is advised to only use the text-caption or text-caption-head variant. Use the Figcaption component only for inline text inside a figure.",
  status: TypographyStatus
};
const TypographyDocumentation = {
  Global,
  Text,
  Figcaption,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  P,
  Small,
  Strong
};
const gitReleaseTag$1 = "1.13.0";
const SkipLinkDocumentation = {
  name: "SkipLink",
  description: "A Skip Link is an accessibility feature that allows keyboard and screen reader users to bypass repetitive elements(for example navigation/search) and jump directly to the main content of a page or to a specific section like filtering on overview pages.",
  status: "stable",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag$1}`,
  gitReleaseTag: gitReleaseTag$1,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/action/SkipLink",
  safariLink: "https://confluence.grandvision.global/spaces/GV/pages/197068632/SkipLink",
  storybookLink: "?path=/docs/components-action-skiplink--docs",
  figmaUrl: "https://www.figma.com/design/E5kmn6g79czOls6RLHIzhq9J/00-UI-Kit?node-id=26257-7957&m=dev"
};
const gitReleaseTag = "2.1.0";
const TextFieldDocumentation = {
  name: "TextField",
  description: "TextField is component that renders an Input with label and messages. It handles the success and error state onBlur via the html ValidityState api.",
  status: "beta",
  gitReleaseUrl: `https://github.com/GrandVisionHQ/iris-ui/releases/tag/v${gitReleaseTag}`,
  gitReleaseTag,
  gitLink: "https://github.com/GrandVisionHQ/iris-ui/tree/main/iris-ui/src/components/input/TextField",
  safariLink: "https://confluence.grandvision.global/spaces/GV/pages/209977559/TextField",
  accessibility: `The TextField component uses React Aria's useTextField hook to provide comprehensive accessibility support. It automatically applies appropriate ARIA attributes and handles keyboard interactions.
  <ul>
    <li><strong>Proper labeling:</strong> Labels are correctly associated with inputs using the label element</li>
    <li><strong>Required field indication:</strong> Required fields are marked with aria-required and visual indicators</li>
    <li><strong>Error announcements:</strong> Error messages are properly announced to screen readers using aria-describedby</li>
    <li><strong>Validation feedback:</strong> HTML5 validation with custom error messages for better user experience</li>
    <li><strong>Keyboard navigation:</strong> Full keyboard support for navigation and interaction</li>
  </ul>

  <h3>Error Handling</h3>
  <ul>
    <li>When a required field is empty, the valueMissingMessage is announced to screen readers</li>
    <li>When a pattern validation fails, the patternMismatchMessage is announced to screen readers</li>
    <li>Error states are visually indicated and programmatically available to assistive technologies</li>
  </ul>`,
  storybookLink: "?path=/docs/components-input-textfield--docs",
  figmaUrl: "https://www.figma.com/design/BpMJb8bjnElARjt0c2WepF/feat--iris-ui-forms?node-id=16942-1799&m=dev"
};
exports.AlertDocumentation = AlertDocumentation;
exports.BadgeDocumentation = BadgeDocumentation;
exports.ButtonDocumentation = ButtonDocumentation;
exports.CheckboxDocumentation = CheckboxDocumentation;
exports.CheckboxGroupDocumentation = CheckboxGroupDocumentation;
exports.DialogDocumentation = DialogDocumentation;
exports.DrawerDocumentation = DrawerDocumentation;
exports.FontLoaderDocumentation = FontLoaderDocumentation;
exports.IconButtonDocumentation = IconButtonDocumentation;
exports.IconDocumentation = IconDocumentation;
exports.MessageDocumentation = MessageDocumentation;
exports.ModalDocumentation = ModalDocumentation;
exports.PriceDocumentation = PriceDocumentation;
exports.ProgressDocumentation = ProgressDocumentation;
exports.SkeletonDocumentation = SkeletonDocumentation;
exports.SkipLinkDocumentation = SkipLinkDocumentation;
exports.SpacingDocumentation = SpacingDocumentation;
exports.SpinnerDocumentation = SpinnerDocumentation;
exports.TextFieldDocumentation = TextFieldDocumentation;
exports.ToastDocumentation = ToastDocumentation;
exports.ToggletipDocumentation = ToggletipDocumentation;
exports.TypographyDocumentation = TypographyDocumentation;
